import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import EMPAccordion from "../../../shared/components/EMPAccordion/EMPAccordion";
import EMPBanner from "../../../shared/components/EMPBanner/EMPBanner";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import EMPConfirmationBanner from "../../../shared/components/EMPConfirmationBanner/EMPConfirmationBanner";
import EMPDialog from "../../../shared/components/EMPDialog/EMPDialog";
import EMPLoader from "../../../shared/components/EMPLoader/EMPLoader";
import EMPRadioCardGroup from "../../../shared/components/EMPRadioCardGroup/EMPRadioCardGroup";
import EMPTextAreaField from "../../../shared/components/EMPTextAreaField/EMPTextAreaField";
import SharedToolTip from "../../../shared/sharedToolTip/SharedToolTip";
import StripeAccountDetails from "./StripeAccountDetails";
import DealerStripeEnrollment from "./DealerStripeEnrollment";
import { StripeProvider } from "../../../shared/context/StripeContext";
import {
  ADVANCED_ACCESS_DEPOSIT_DISPLAY,
  CHECKOUT_DEPOSIT_OPTIONS,
  MAX_ACH_OPTIONS,
  MAX_CREDIT_CARD_OPTIONS,
  ORDER_DEPOSIT_OPTIONS,
  STRIPE_ACCOUNT_URL,
  mapDepositAmountFromApi,
} from "./constants";
import styles from "./OnboardingPaymentProcessorOnlineDeposits.module.scss";
import { UserContext } from "../../../shared/context/userContext";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";
import OnboardingPageTitle from "../../../shared/components/OnboardingPageTitle/OnboardingPageTitle";
import accordionStyles from "../../../shared/components/EMPAccordion/EMPAccordionLincoln.module.scss";
import DealerOnboardingClient from "../../../clients/DealerOnboardingClient";
import StripeOnboardingClient, {
  DEALER_STRIPE_DETAILS_QUERY_KEY,
} from "../../../clients/StripeOnboardingClient";
import {
  STRIPE_ACCOUNT_LOOKUP_QUERY_KEY,
  isStripeAccountNotFoundError,
  lookupStripeAccount,
  shouldRetryStripeAccountLookup,
} from "../../../shared/services/stripe/StripeService";
import {
  DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
  findStripeDepositAmountsSubstep,
  findStripePreferenceSubstep,
  formatOnboardingCompletedDate,
  mapProgressStripePreferenceToRadioSelection,
} from "../../../shared/utils/onboardingProgressUtil";
import {
  findDealerGroupRowByBrand,
  hasStripeOnboardingDbRecord,
  isEnrollmentTermsCompleted,
  isStripePreferenceStepCompleted,
  shouldBlockLincolnStripeSetupUntilFord,
} from "../../../shared/utils/dualDealerStripeSetupGate";
import {
  findDealerInGroupByContext,
  mapDealerGroupRowToAddressPayload,
} from "../../../shared/utils/dealerGroupSelection";
import { publish } from "../../../common/PubSub";

const I18N_BASE = "DealerOnboarding.content.paymentProcessorOnlineDeposits";

const EMPTY_DEPOSIT_AMOUNTS = {
  orderDeposit: "",
  maximumAcceptedCreditCard: "",
  maximumAcceptedAch: "",
  checkoutDeposit: "",
};

const NOT_APPLICABLE_SELECT_SX = {
  borderRadius: "8px",
  backgroundColor: "var(--color-fill-onlight-subtle, #E5E5E5) !important",
  opacity: 1,
  "&.Mui-disabled": {
    backgroundColor: "var(--color-fill-onlight-subtle, #E5E5E5) !important",
    opacity: 1,
    WebkitTextFillColor: "#808080",
  },
  "& .MuiSelect-select": {
    backgroundColor: "var(--color-fill-onlight-subtle, #E5E5E5) !important",
    color: "#808080",
    WebkitTextFillColor: "#808080",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "var(--border-width-xs, 1px)",
    borderColor: "var(--color-stroke-onlight-moderate-default, #808080)",
  },
  "& .MuiSelect-icon": {
    color: "#808080",
  },
};

const mapSavedDepositAmounts = ({
  savedOrderDeposit,
  savedMaxCreditCardAmount,
  savedMaxAchAmount,
  savedCartCheckoutDeposit,
}) => ({
  orderDeposit: mapDepositAmountFromApi(savedOrderDeposit),
  maximumAcceptedCreditCard: mapDepositAmountFromApi(savedMaxCreditCardAmount),
  maximumAcceptedAch: mapDepositAmountFromApi(savedMaxAchAmount),
  checkoutDeposit: mapDepositAmountFromApi(savedCartCheckoutDeposit),
});

const fetchStripeAccountLookup = async (dealerCommonId) => {
  try {
    return await lookupStripeAccount(dealerCommonId);
  } catch (error) {
    if (isStripeAccountNotFoundError(error)) {
      return null;
    }
    throw error;
  }
};

const toNonEmptyString = (value) => {
  if (value == null) {
    return null;
  }
  const stringValue = String(value).trim();
  return stringValue === "" ? null : stringValue;
};

const computeStripeAccountConnection = ({
  stripeDetails,
  lookedUpStripeAccount,
}) => {
  const persistedStripeAccountId = toNonEmptyString(
    stripeDetails?.stripeAccountId
  );

  const isStripeAccountConnected =
    Boolean(persistedStripeAccountId) &&
    (stripeDetails?.status == null ||
      String(stripeDetails.status).toLowerCase() === "active");

  const lookedUpStripeAccountId = toNonEmptyString(lookedUpStripeAccount?.id);

  return {
    persistedStripeAccountId,
    isStripeAccountConnected,
    lookedUpStripeAccountId,
  };
};

const computeStripeSetupFlags = ({
  effectiveSavedPreference,
  isStripeAccountConnected,
  isEditingStripePreferences,
  stripePaymentProcessor,
  isFordBrand,
  isDualDealer,
  lookedUpStripeAccountId,
  persistedStripeAccountId,
  isStripeMutationBusy,
  dealerCommonId,
  isStripeLookupSettled,
  isStripeEnrollmentActive,
  hasStripeEnrollmentCompletedThisSession,
  stripeAccountFullyEnabled,
}) => {
  const stripeSetupCompleted =
    effectiveSavedPreference === "no" ||
    (effectiveSavedPreference === "yes" && isStripeAccountConnected);

  // Right after the dealer finishes Stripe's onboarding screens in this
  // session, our backend already treats the account as "connected", but
  // Stripe may still be reviewing it. Keep showing the enrollment widget
  // (with its "information in review" banner) until Stripe itself reports
  // the account fully enabled, instead of switching to the plain
  // account-details table. Scoped to this session only - a dealer whose
  // account was already connected in an earlier session should just see
  // the account-details table immediately, not re-enter the enrollment flow.
  const isAwaitingStripeReview =
    isStripeAccountConnected &&
    hasStripeEnrollmentCompletedThisSession &&
    !stripeAccountFullyEnabled;

  const isStripePreferenceSavedState =
    Boolean(effectiveSavedPreference) &&
    stripePaymentProcessor === effectiveSavedPreference &&
    !isEditingStripePreferences;

  const isEditingWithNoChange =
    isEditingStripePreferences &&
    stripePaymentProcessor === effectiveSavedPreference;

  const isSaveStripeSetupDisabled =
    !stripePaymentProcessor ||
    isStripeMutationBusy ||
    !dealerCommonId ||
    isStripePreferenceSavedState ||
    isEditingWithNoChange;

  const isDisconnectingFromYes =
    isEditingStripePreferences &&
    effectiveSavedPreference === "yes" &&
    stripePaymentProcessor === "no";

  const showStripeAccountCreateFlow =
    effectiveSavedPreference === "yes" &&
    !isEditingStripePreferences &&
    isStripeLookupSettled &&
    ((!stripeSetupCompleted &&
      (!lookedUpStripeAccountId || isStripeEnrollmentActive)) ||
      isAwaitingStripeReview);

  // A dual dealer viewing Lincoln onboarding connects an existing Stripe
  // account through the EIN selection cards instead of the plain connect card.
  const canConnectLookedUpStripeAccount =
    !stripeSetupCompleted &&
    effectiveSavedPreference === "yes" &&
    Boolean(lookedUpStripeAccountId) &&
    !isEditingStripePreferences &&
    !isStripeEnrollmentActive;

  const isDualLincolnStripeSetup = isDualDealer && !isFordBrand;

  return {
    stripeSetupCompleted,
    isStripePreferenceSavedState,
    isSaveStripeSetupDisabled,
    isDisconnectingFromYes,
    showInitialLincolnNoCaution:
      !isFordBrand &&
      stripePaymentProcessor === "no" &&
      !stripeSetupCompleted &&
      !isEditingStripePreferences,
    showEditYesToNoCaution:
      isEditingStripePreferences &&
      effectiveSavedPreference === "yes" &&
      stripePaymentProcessor === "no",
    showStripeConnectDetails:
      canConnectLookedUpStripeAccount && !isDualLincolnStripeSetup,
    showEinSelection:
      canConnectLookedUpStripeAccount && isDualLincolnStripeSetup,
    showStripeAccountCreateFlow,
    showStripeAccountDetails:
      stripeSetupCompleted &&
      effectiveSavedPreference === "yes" &&
      !isAwaitingStripeReview,
    showStripeDeclinedDetails:
      stripeSetupCompleted && effectiveSavedPreference === "no",
    declinedVariant: persistedStripeAccountId
      ? "disconnected"
      : "neverConnected",
    showInitialSetupForm: !stripeSetupCompleted,
    showEditSetupForm: isEditingStripePreferences,
  };
};

const computeDepositFlags = ({
  depositAmounts,
  savedDepositAmounts,
  stripeSetupCompleted,
  effectiveSavedPreference,
  isEditingStripePreferences,
  stripeDepositAmountsSubstep,
  showDepositSavedBanner,
  isDepositMutationBusy,
  dealerCommonId,
}) => {
  const hasPendingDepositChange =
    depositAmounts.orderDeposit !== savedDepositAmounts.orderDeposit ||
    depositAmounts.maximumAcceptedCreditCard !==
      savedDepositAmounts.maximumAcceptedCreditCard ||
    depositAmounts.maximumAcceptedAch !==
      savedDepositAmounts.maximumAcceptedAch ||
    depositAmounts.checkoutDeposit !== savedDepositAmounts.checkoutDeposit;

  const hasAllDepositSelections =
    Boolean(depositAmounts.orderDeposit) &&
    Boolean(depositAmounts.maximumAcceptedCreditCard) &&
    Boolean(depositAmounts.maximumAcceptedAch) &&
    Boolean(depositAmounts.checkoutDeposit);

  const hasSavedDepositSelections =
    Boolean(savedDepositAmounts.orderDeposit) &&
    Boolean(savedDepositAmounts.maximumAcceptedCreditCard) &&
    Boolean(savedDepositAmounts.maximumAcceptedAch) &&
    Boolean(savedDepositAmounts.checkoutDeposit);

  const depositAmountsCompleted =
    stripeDepositAmountsSubstep?.status === "completed" ||
    hasSavedDepositSelections;

  const isDepositAmountsLockedPendingPreference = !effectiveSavedPreference;

  const isDepositAmountsNotApplicable =
    stripeSetupCompleted &&
    effectiveSavedPreference === "no" &&
    !isEditingStripePreferences;

  const areDepositFieldsDisabled =
    isDepositAmountsLockedPendingPreference || isDepositAmountsNotApplicable;

  const isDepositAmountsSavedState =
    isDepositAmountsNotApplicable ||
    (showDepositSavedBanner && !hasPendingDepositChange) ||
    (depositAmountsCompleted && !hasPendingDepositChange);

  const isSaveDepositAmountsDisabled =
    areDepositFieldsDisabled ||
    isDepositMutationBusy ||
    !dealerCommonId ||
    !hasAllDepositSelections ||
    !hasPendingDepositChange;

  return {
    hasPendingDepositChange,
    hasAllDepositSelections,
    depositAmountsCompleted,
    isDepositAmountsLockedPendingPreference,
    isDepositAmountsNotApplicable,
    areDepositFieldsDisabled,
    isDepositAmountsSavedState,
    isSaveDepositAmountsDisabled,
  };
};

const getDepositSaveButtonLabel = (t, { isSaving, isSavedState }) => {
  if (isSaving) {
    return t(`${I18N_BASE}.depositSection.saving`);
  }
  if (isSavedState) {
    return t(`${I18N_BASE}.depositSection.saved`);
  }
  return t(`${I18N_BASE}.depositSection.save`);
};

const getStripeSaveButtonLabel = (t, { isSaving, isSavedState }) => {
  if (isSaving) {
    return t(`${I18N_BASE}.savingButtonLabel`);
  }
  if (isSavedState) {
    return t(`${I18N_BASE}.stripeSetup.saved`);
  }
  return t(`${I18N_BASE}.stripeSetup.save`);
};

const useStripeOnboardingMutations = ({
  dealerCommonId,
  queryClient,
  t,
  actions,
}) => {
  const saveStripeMutation = useMutation({
    mutationFn: (payload) =>
      DealerOnboardingClient.saveStripePreference(dealerCommonId, payload),
    onSuccess: (_data, variables) => {
      const savedSelection = mapProgressStripePreferenceToRadioSelection(
        variables.stripePreference
      );
      actions.setLocalSavedPreference(savedSelection);
      actions.setStripePaymentProcessor(savedSelection);
      actions.setSaveError(null);
      actions.setIsEditingStripePreferences(false);
      actions.setShowPreferencesUpdatedBanner(savedSelection === "no");
      queryClient.invalidateQueries({
        queryKey: [DEALER_ONBOARDING_PROGRESS_QUERY_KEY, dealerCommonId],
      });
      if (savedSelection === "yes") {
        queryClient.invalidateQueries({
          queryKey: [STRIPE_ACCOUNT_LOOKUP_QUERY_KEY, dealerCommonId],
        });
      }
    },
    onError: () => {
      actions.setSaveError(t(`${I18N_BASE}.saveError`));
      actions.setShowPreferencesUpdatedBanner(false);
    },
  });

  const saveDepositAmountsMutation = useMutation({
    mutationFn: (payload) =>
      StripeOnboardingClient.saveDepositAmounts(dealerCommonId, payload),
    onSuccess: (_data, variables) => {
      const nextSavedAmounts = {
        orderDeposit: String(variables.orderDeposit),
        maximumAcceptedCreditCard: String(variables.maxCreditCardAmount),
        maximumAcceptedAch: String(variables.maxAchAmount),
        checkoutDeposit: String(variables.cartCheckoutDeposit),
      };
      actions.setSavedDepositAmounts(nextSavedAmounts);
      actions.setDepositAmounts(nextSavedAmounts);
      actions.setDepositSaveError(null);
      actions.setShowDepositSavedBanner(true);
      queryClient.invalidateQueries({
        queryKey: [DEALER_ONBOARDING_PROGRESS_QUERY_KEY, dealerCommonId],
      });
      queryClient.invalidateQueries({
        queryKey: [DEALER_STRIPE_DETAILS_QUERY_KEY, dealerCommonId],
      });
    },
    onError: () => {
      actions.setShowDepositSavedBanner(false);
      actions.setDepositSaveError(t(`${I18N_BASE}.saveError`));
    },
  });

  const connectStripeAccountMutation = useMutation({
    mutationFn: (payload) =>
      StripeOnboardingClient.connectStripeAccount(dealerCommonId, payload),
    onSuccess: (data) => {
      actions.setConnectError(null);
      actions.setShowStripeAccountSetupBanner(true);
      if (data) {
        queryClient.setQueryData(
          [DEALER_STRIPE_DETAILS_QUERY_KEY, dealerCommonId],
          (previous) => ({ ...(previous || {}), ...data })
        );
      }
      queryClient.invalidateQueries({
        queryKey: [DEALER_ONBOARDING_PROGRESS_QUERY_KEY, dealerCommonId],
      });
    },
    onError: () => {
      actions.setConnectError(t(`${I18N_BASE}.saveError`));
      actions.setShowStripeAccountSetupBanner(false);
    },
  });

  const disconnectStripeMutation = useMutation({
    mutationFn: (payload) =>
      StripeOnboardingClient.disconnectStripePaymentProcessor(
        dealerCommonId,
        payload
      ),
    onSuccess: (data) => {
      actions.setLocalSavedPreference("no");
      actions.setStripePaymentProcessor("no");
      actions.setIsEditingStripePreferences(false);
      actions.setIsDisconnectModalOpen(false);
      actions.setShowPreferencesUpdatedBanner(false);
      actions.setShowStripeDisconnectedBanner(true);
      actions.setSaveError(null);
      actions.setDepositAmounts(EMPTY_DEPOSIT_AMOUNTS);
      actions.setSavedDepositAmounts(EMPTY_DEPOSIT_AMOUNTS);
      queryClient.setQueryData(
        [DEALER_STRIPE_DETAILS_QUERY_KEY, dealerCommonId],
        (previous) => ({
          ...(previous || {}),
          ...(data || {}),
          orderDeposit: null,
          maxCreditCardAmount: null,
          maxAchAmount: null,
          cartCheckoutDeposit: null,
        })
      );
      queryClient.invalidateQueries({
        queryKey: [DEALER_ONBOARDING_PROGRESS_QUERY_KEY, dealerCommonId],
      });
      queryClient.invalidateQueries({
        queryKey: [DEALER_STRIPE_DETAILS_QUERY_KEY, dealerCommonId],
      });
    },
    onError: () => {
      actions.setSaveError(t(`${I18N_BASE}.saveError`));
      actions.setIsDisconnectModalOpen(false);
    },
  });

  return {
    saveStripeMutation,
    saveDepositAmountsMutation,
    connectStripeAccountMutation,
    disconnectStripeMutation,
  };
};

const InfoTooltip = ({ title, copy, testId }) => (
  <span
    className={styles.infoTooltipWrapper}
    data-testid={testId}
    aria-label={`${title} information`}
  >
    <SharedToolTip
      title={title}
      body={copy}
      placement="right"
      iconColor="#485b65"
      fontSize="10"
      showCloseIcon
      useOutlinedInfoIcon
      ariaLabel={`${title} information`}
    />
  </span>
);

InfoTooltip.propTypes = {
  title: PropTypes.string.isRequired,
  copy: PropTypes.string.isRequired,
  testId: PropTypes.string,
};

InfoTooltip.defaultProps = {
  testId: undefined,
};

const DepositDropdownField = ({
  id,
  label,
  value,
  onChange,
  options,
  infoTitle,
  infoCopy,
  infoTestId,
  selectTestId,
  disabled,
  displayNotApplicable,
  notApplicableLabel,
  selectPlaceholderLabel,
}) => (
  <Box className={styles.depositField}>
    <Box className={styles.depositLabelRow}>
      <Typography component="label" htmlFor={id} className={styles.fieldLabel}>
        {label}
      </Typography>
      {InfoTooltip({ title: infoTitle, copy: infoCopy, testId: infoTestId })}
    </Box>

    <FormControl
      className={`${styles.depositControl}${
        displayNotApplicable ? ` ${styles.depositControlNotApplicable}` : ""
      }`}
      disabled={disabled}
    >
      <Select
        id={id}
        value={displayNotApplicable ? "" : value}
        onChange={onChange}
        displayEmpty
        disabled={disabled}
        className={styles.depositSelect}
        sx={displayNotApplicable ? NOT_APPLICABLE_SELECT_SX : undefined}
        inputProps={{
          "data-testid": selectTestId,
          "aria-label": label,
        }}
        renderValue={(selected) => {
          if (displayNotApplicable || selected === "") {
            return (
              <span className={styles.placeholderText}>
                {displayNotApplicable
                  ? notApplicableLabel
                  : selectPlaceholderLabel}
              </span>
            );
          }
          const option = options.find((item) => item.value === selected);
          return option?.label || selected;
        }}
      >
        <MenuItem value="">
          <span className={styles.placeholderText}>
            {displayNotApplicable ? notApplicableLabel : selectPlaceholderLabel}
          </span>
        </MenuItem>

        {!displayNotApplicable &&
          options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  </Box>
);

DepositDropdownField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  infoTitle: PropTypes.string.isRequired,
  infoCopy: PropTypes.string.isRequired,
  infoTestId: PropTypes.string.isRequired,
  selectTestId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  displayNotApplicable: PropTypes.bool,
  notApplicableLabel: PropTypes.string.isRequired,
  selectPlaceholderLabel: PropTypes.string.isRequired,
};

DepositDropdownField.defaultProps = {
  value: "",
  disabled: false,
  displayNotApplicable: false,
};

const AdvancedAccessDepositField = ({
  isNotApplicable,
  notApplicableLabel,
  t,
}) => (
  <Box className={styles.depositField}>
    <Box className={styles.depositLabelRow}>
      <Typography
        component="label"
        htmlFor="advanced-access-deposit"
        className={styles.fieldLabel}
      >
        {t(`${I18N_BASE}.depositSection.advancedAccessDeposit.label`)}
      </Typography>
      {InfoTooltip({
        title: t(`${I18N_BASE}.depositSection.advancedAccessDeposit.label`),
        copy: t(`${I18N_BASE}.depositSection.advancedAccessDeposit.infoCopy`, {
          currentBrand: notApplicableLabel.currentBrand,
        }),
        testId: "advanced-access-deposit-info",
      })}
    </Box>

    <Box
      className={
        isNotApplicable ? styles.depositControlNotApplicable : undefined
      }
    >
      <EMPTextAreaField
        value={
          isNotApplicable
            ? notApplicableLabel.label
            : ADVANCED_ACCESS_DEPOSIT_DISPLAY
        }
        disabled
        testId="advanced-access-deposit-input"
      />
    </Box>

    {!isNotApplicable && (
      <Typography className={styles.oemText}>
        {t(`${I18N_BASE}.depositSection.advancedAccessDeposit.oemText`)}
      </Typography>
    )}
  </Box>
);

AdvancedAccessDepositField.propTypes = {
  isNotApplicable: PropTypes.bool.isRequired,
  notApplicableLabel: PropTypes.shape({
    label: PropTypes.string,
    currentBrand: PropTypes.string,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

const StripePreferenceForm = ({
  includeSetupDescription,
  includeCancel,
  t,
  brandName,
  radioColor,
  stripePaymentProcessor,
  onPaymentProcessorChange,
  saveError,
  onSave,
  isSaveDisabled,
  isSaving,
  isSavedState,
  onCancel,
}) => (
  <Box className={styles.copyContainer}>
    {includeSetupDescription && (
      <Typography variant="body1" className={styles.copy}>
        {t(`${I18N_BASE}.stripeSetup.description`, { currentBrand: brandName })}{" "}
        {t(`${I18N_BASE}.stripeSetup.selectCopyPrefix`)}{" "}
        <strong>{t(`${I18N_BASE}.stripeSetup.startStripeSetup`)}</strong>{" "}
        {t(`${I18N_BASE}.stripeSetup.selectCopySuffix`)}
      </Typography>
    )}

    <Box className={styles.questionAndControls}>
      <Typography variant="body1" className={styles.copy}>
        {t(`${I18N_BASE}.stripeSetup.paymentProcessorQuestion`)}
      </Typography>

      <FormControl component="fieldset">
        <RadioGroup
          name="stripe-payment-processor"
          value={stripePaymentProcessor}
          onChange={onPaymentProcessorChange}
          className={styles.radioGroup}
          data-testid="stripe-payment-processor-radio-group"
        >
          <FormControlLabel
            value="yes"
            control={
              <Radio
                className={styles.radioButton}
                sx={{ "&.Mui-checked": { color: radioColor } }}
              />
            }
            label={t(`${I18N_BASE}.stripeSetup.yes`)}
            className={styles.radioOption}
            data-testid="stripe-payment-processor-yes"
          />
          <FormControlLabel
            value="no"
            control={
              <Radio
                className={styles.radioButton}
                sx={{ "&.Mui-checked": { color: radioColor } }}
              />
            }
            label={t(`${I18N_BASE}.stripeSetup.no`)}
            className={styles.radioOption}
            data-testid="stripe-payment-processor-no"
          />
        </RadioGroup>
      </FormControl>

      {saveError && (
        <Typography
          color="error"
          variant="body2"
          data-testid="stripe-setup-save-error"
        >
          {saveError}
        </Typography>
      )}

      <Box className={styles.saveButtonContainer}>
        <EMPButton
          variant="contained"
          onClick={onSave}
          disabled={isSaveDisabled}
          testId="stripe-setup-save-button"
          className={styles.saveButton}
        >
          {getStripeSaveButtonLabel(t, { isSaving, isSavedState })}
        </EMPButton>

        {includeCancel && (
          <Button
            variant="text"
            onClick={onCancel}
            data-testid="stripe-setup-cancel-button"
            className={styles.cancelButton}
          >
            {t(`${I18N_BASE}.stripeSetup.cancel`)}
          </Button>
        )}
      </Box>
    </Box>
  </Box>
);

StripePreferenceForm.propTypes = {
  includeSetupDescription: PropTypes.bool,
  includeCancel: PropTypes.bool,
  t: PropTypes.func.isRequired,
  brandName: PropTypes.string.isRequired,
  radioColor: PropTypes.string,
  stripePaymentProcessor: PropTypes.string,
  onPaymentProcessorChange: PropTypes.func.isRequired,
  saveError: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  isSaveDisabled: PropTypes.bool,
  isSaving: PropTypes.bool,
  isSavedState: PropTypes.bool,
  onCancel: PropTypes.func,
};

StripePreferenceForm.defaultProps = {
  includeSetupDescription: false,
  includeCancel: false,
  radioColor: undefined,
  stripePaymentProcessor: "",
  saveError: null,
  isSaveDisabled: false,
  isSaving: false,
  isSavedState: false,
  onCancel: undefined,
};

const StripeAccountDetailsSection = ({
  showStripeConnectDetails,
  showStripeAccountDetails,
  showStripeDeclinedDetails,
  lookedUpStripeAccountId,
  stripeAccountIdForDisplay,
  completedDateDisplay,
  connectError,
  onConnect,
  isConnecting,
  brandName,
  onEdit,
  showEditPreferences,
  showStripeAccountSetupBanner,
  onDismissSuccessBanner,
  declinedVariant,
  showStripeDisconnectedBanner,
  onDismissDisconnectedBanner,
  showPreferencesUpdatedBanner,
  onDismissPreferencesUpdatedBanner,
}) => (
  <Box id="stripe-account-details">
    {showStripeConnectDetails && (
      <>
        <StripeAccountDetails
          mode="connect"
          accountId={lookedUpStripeAccountId}
          completedDate=""
          onConnect={onConnect}
          isConnecting={isConnecting}
          stripeUrl={STRIPE_ACCOUNT_URL}
          brandName={brandName}
        />
        {connectError && (
          <Typography
            color="error"
            variant="body2"
            data-testid="stripe-connect-error"
          >
            {connectError}
          </Typography>
        )}
      </>
    )}

    {showStripeAccountDetails && (
      <StripeAccountDetails
        mode="connected"
        accountId={stripeAccountIdForDisplay}
        completedDate={completedDateDisplay}
        onEdit={onEdit}
        showEditPreferences={showEditPreferences}
        stripeUrl={STRIPE_ACCOUNT_URL}
        brandName={brandName}
        showSuccessBanner={showStripeAccountSetupBanner}
        onDismissSuccessBanner={onDismissSuccessBanner}
      />
    )}

    {showStripeDeclinedDetails && (
      <StripeAccountDetails
        mode="declined"
        accountId={stripeAccountIdForDisplay}
        completedDate={completedDateDisplay}
        onEdit={onEdit}
        showEditPreferences={showEditPreferences}
        stripeUrl={STRIPE_ACCOUNT_URL}
        brandName={brandName}
        declinedVariant={declinedVariant}
        showDisconnectedBanner={showStripeDisconnectedBanner}
        onDismissDisconnectedBanner={onDismissDisconnectedBanner}
        showPreferencesUpdatedBanner={showPreferencesUpdatedBanner}
        onDismissPreferencesUpdatedBanner={onDismissPreferencesUpdatedBanner}
      />
    )}
  </Box>
);

StripeAccountDetailsSection.propTypes = {
  showStripeConnectDetails: PropTypes.bool.isRequired,
  showStripeAccountDetails: PropTypes.bool.isRequired,
  showStripeDeclinedDetails: PropTypes.bool.isRequired,
  lookedUpStripeAccountId: PropTypes.string,
  stripeAccountIdForDisplay: PropTypes.string,
  completedDateDisplay: PropTypes.string,
  connectError: PropTypes.string,
  onConnect: PropTypes.func.isRequired,
  isConnecting: PropTypes.bool,
  brandName: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  showEditPreferences: PropTypes.bool,
  showStripeAccountSetupBanner: PropTypes.bool,
  onDismissSuccessBanner: PropTypes.func.isRequired,
  declinedVariant: PropTypes.string,
  showStripeDisconnectedBanner: PropTypes.bool,
  onDismissDisconnectedBanner: PropTypes.func.isRequired,
  showPreferencesUpdatedBanner: PropTypes.bool,
  onDismissPreferencesUpdatedBanner: PropTypes.func.isRequired,
};

StripeAccountDetailsSection.defaultProps = {
  lookedUpStripeAccountId: null,
  stripeAccountIdForDisplay: null,
  completedDateDisplay: "",
  connectError: null,
  isConnecting: false,
  showEditPreferences: true,
  showStripeAccountSetupBanner: false,
  declinedVariant: "neverConnected",
  showStripeDisconnectedBanner: false,
  showPreferencesUpdatedBanner: false,
};

const StripeDisconnectModal = ({
  isOpen,
  t,
  isFordBrand,
  onClose,
  onConfirm,
  isConfirming,
}) => (
  <EMPDialog
    open={isOpen}
    dialogTitle={t(
      `${I18N_BASE}.stripeSetup.accountDetails.disconnectModal.title`
    )}
    hasCloseIcon
    handleClose={onClose}
    isFord={isFordBrand}
    maxWidth={false}
    paperClassName={styles.disconnectModalPaper}
    hasSecondaryActionButton
    secondaryActionButtonText={t(
      `${I18N_BASE}.stripeSetup.accountDetails.disconnectModal.cancel`
    )}
    secondaryActionButtonVariant={isFordBrand ? "outlined" : "contained"}
    secondaryActionButtonClassName={
      isFordBrand ? undefined : styles.disconnectModalCancelButton
    }
    onSecondaryAction={onClose}
    hasActionButton
    actionButtonText={t(
      `${I18N_BASE}.stripeSetup.accountDetails.disconnectModal.disconnect`
    )}
    actionButtonVariant={isFordBrand ? "contained" : "outlined"}
    actionButtonClassName={
      isFordBrand ? undefined : styles.disconnectModalDisconnectButton
    }
    onConfirm={onConfirm}
    disableActionButton={isConfirming}
    actionLoading={isConfirming}
    dialogActionsDataTestId="stripe-disconnect-modal-actions"
    dialogActionsClassName={
      isFordBrand ? undefined : styles.disconnectModalActions
    }
  >
    <Box data-testid="stripe-disconnect-modal-body">
      <Typography className={styles.copy}>
        {t(
          `${I18N_BASE}.stripeSetup.accountDetails.disconnectModal.bodyParagraph1`
        )}
      </Typography>
      <Typography
        className={`${styles.copy} ${styles.disconnectModalParagraph}`}
      >
        {t(
          `${I18N_BASE}.stripeSetup.accountDetails.disconnectModal.bodyParagraph2`
        )}
      </Typography>
      <Typography
        className={`${styles.copy} ${styles.disconnectModalParagraph}`}
      >
        {t(
          `${I18N_BASE}.stripeSetup.accountDetails.disconnectModal.bodyParagraph3`
        )}
      </Typography>
    </Box>
  </EMPDialog>
);

StripeDisconnectModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  isFordBrand: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isConfirming: PropTypes.bool,
};

StripeDisconnectModal.defaultProps = {
  isConfirming: false,
};

const OnboardingPaymentProcessorOnlineDeposits = ({
  savedStripePreference = null,
  progressSteps = null,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useContext(UserContext);
  const isFordBrand = IS_FORD_BRAND(user?.brand);
  const brandName = isFordBrand ? "Ford" : "Lincoln";
  const brandRadioColor = isFordBrand ? "#066FEF" : "#485B65";
  const dealerCommonId =
    user?.dealerCommonId || user?.commonId || user?.commonDealerId;
  const updatedBy = user?.userId || user?.commonName;
  const dealerGroup = useMemo(() => user?.dealerGroup || [], [user]);
  const fordDealerRow = findDealerGroupRowByBrand(dealerGroup, "F");
  const lincolnDealerRow = findDealerGroupRowByBrand(dealerGroup, "L");
  const isDualDealer = Boolean(fordDealerRow && lincolnDealerRow);
  const fordCommonId = fordDealerRow?.commonId || null;

  const mappedSavedPreference = mapProgressStripePreferenceToRadioSelection(
    savedStripePreference
  );

  const [expandedStripeSetup, setExpandedStripeSetup] = useState(
    () => isFordBrand || !isDualDealer
  );
  const [pendingStripeExpand, setPendingStripeExpand] = useState(false);
  const [expandedDepositAmounts, setExpandedDepositAmounts] = useState(false);
  const [stripePaymentProcessor, setStripePaymentProcessor] = useState(
    mappedSavedPreference
  );
  const [localSavedPreference, setLocalSavedPreference] = useState(
    mappedSavedPreference
  );
  const [isEditingStripePreferences, setIsEditingStripePreferences] =
    useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showPreferencesUpdatedBanner, setShowPreferencesUpdatedBanner] =
    useState(false);
  const [showStripeAccountSetupBanner, setShowStripeAccountSetupBanner] =
    useState(false);
  const [showStripeDisconnectedBanner, setShowStripeDisconnectedBanner] =
    useState(false);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isFordFirstSetupModalOpen, setIsFordFirstSetupModalOpen] =
    useState(false);
  const [depositAmounts, setDepositAmounts] = useState(EMPTY_DEPOSIT_AMOUNTS);
  const [savedDepositAmounts, setSavedDepositAmounts] = useState(
    EMPTY_DEPOSIT_AMOUNTS
  );
  const [showDepositSavedBanner, setShowDepositSavedBanner] = useState(false);
  const [depositSaveError, setDepositSaveError] = useState(null);
  const [connectError, setConnectError] = useState(null);
  const [einSelection, setEinSelection] = useState("");
  const [isStripeEnrollmentActive, setIsStripeEnrollmentActive] =
    useState(false);
  const [stripeAccountFullyEnabled, setStripeAccountFullyEnabled] =
    useState(false);
  const [
    hasStripeEnrollmentCompletedThisSession,
    setHasStripeEnrollmentCompletedThisSession,
  ] = useState(false);

  const effectiveSavedPreference =
    localSavedPreference || mappedSavedPreference;

  const { data: stripeDetails = null, isFetched: isStripeDetailsFetched } =
    useQuery({
      queryKey: [DEALER_STRIPE_DETAILS_QUERY_KEY, dealerCommonId],
      queryFn: () => StripeOnboardingClient.getStripeDetails(dealerCommonId),
      enabled: Boolean(dealerCommonId),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    });

  const fordProgressRequest = useMemo(() => {
    if (isFordBrand || !isDualDealer || !fordDealerRow || !fordCommonId) {
      return null;
    }
    const matchingDealer =
      findDealerInGroupByContext(dealerGroup, fordCommonId, user) ||
      fordDealerRow;
    const addressPayload = mapDealerGroupRowToAddressPayload(matchingDealer);
    if (!matchingDealer?.dealerName || !user?.market) {
      return null;
    }
    return {
      dealerCommonId: fordCommonId,
      dealerInfo: {
        dealerCommonId: fordCommonId,
        dealerName: matchingDealer.dealerName || matchingDealer.name,
        brand: "F",
        dualDealer: true,
        market: user.market,
        paCode: matchingDealer.paCode || user.dealerId,
        salesCode: matchingDealer.salesCode,
        dealerType: matchingDealer.dealerType,
        ...(user.userId ? { userId: user.userId } : {}),
        ...addressPayload,
      },
    };
  }, [
    dealerGroup,
    fordCommonId,
    fordDealerRow,
    isDualDealer,
    isFordBrand,
    user,
  ]);

  const shouldFetchFordGateData = Boolean(fordProgressRequest);

  const {
    data: fordStripeDetails = null,
    isFetched: isFordStripeDetailsFetched,
  } = useQuery({
    queryKey: [DEALER_STRIPE_DETAILS_QUERY_KEY, fordCommonId],
    queryFn: () => StripeOnboardingClient.getStripeDetails(fordCommonId),
    enabled: shouldFetchFordGateData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { data: fordProgressData = null, isFetched: isFordProgressFetched } =
    useQuery({
      queryKey: [
        DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
        fordProgressRequest?.dealerCommonId,
        "ford-stripe-setup-gate",
      ],
      queryFn: () =>
        DealerOnboardingClient.getDealerOnboardingProgress(
          fordProgressRequest.dealerCommonId,
          fordProgressRequest.dealerInfo
        ),
      enabled: shouldFetchFordGateData,
      select: (response) => response?.data ?? null,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    });

  const fordGateDataReady =
    !shouldFetchFordGateData ||
    (isFordStripeDetailsFetched && isFordProgressFetched);

  const isFordGateLoading = shouldFetchFordGateData && !fordGateDataReady;

  const shouldBlockLincolnStripeSetup =
    fordGateDataReady &&
    shouldBlockLincolnStripeSetupUntilFord({
      isLincolnBrand: !isFordBrand,
      isDualDealer,
      lincolnTermsCompleted: isEnrollmentTermsCompleted(progressSteps),
      fordTermsCompleted: isEnrollmentTermsCompleted(fordProgressData?.steps),
      lincolnHasStripeRecord: hasStripeOnboardingDbRecord(stripeDetails),
      fordHasStripeRecord: hasStripeOnboardingDbRecord(fordStripeDetails),
      fordStripeSetupCompleted: isStripePreferenceStepCompleted(
        fordProgressData?.steps
      ),
    });

  useEffect(() => {
    if (shouldBlockLincolnStripeSetup) {
      setExpandedStripeSetup(false);
    }
  }, [shouldBlockLincolnStripeSetup]);

  // Once the Ford gate data arrives for a pending open, either surface the
  // Ford-first modal (blocked) or leave the accordion expanded (allowed).
  useEffect(() => {
    if (!pendingStripeExpand || isFordGateLoading) {
      return;
    }
    setPendingStripeExpand(false);
    if (shouldBlockLincolnStripeSetup) {
      setIsFordFirstSetupModalOpen(true);
    }
  }, [pendingStripeExpand, isFordGateLoading, shouldBlockLincolnStripeSetup]);

  const { persistedStripeAccountId, isStripeAccountConnected } =
    computeStripeAccountConnection({
      stripeDetails,
      lookedUpStripeAccount: null,
    });

  const isStripeLookupEnabled =
    Boolean(dealerCommonId) &&
    effectiveSavedPreference === "yes" &&
    isStripeDetailsFetched &&
    !persistedStripeAccountId;

  const {
    data: lookedUpStripeAccount = null,
    isFetched: isStripeLookupFetched,
  } = useQuery({
    queryKey: [STRIPE_ACCOUNT_LOOKUP_QUERY_KEY, dealerCommonId],
    queryFn: () => fetchStripeAccountLookup(dealerCommonId),
    enabled: isStripeLookupEnabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: shouldRetryStripeAccountLookup,
  });

  const isStripeLookupSettled = !isStripeLookupEnabled || isStripeLookupFetched;

  const { lookedUpStripeAccountId } = computeStripeAccountConnection({
    stripeDetails,
    lookedUpStripeAccount,
  });

  useEffect(() => {
    if (isStripeAccountConnected) {
      setIsStripeEnrollmentActive(false);
    }
  }, [isStripeAccountConnected]);

  const stripePreferenceSubstep = findStripePreferenceSubstep(progressSteps);
  const stripeDepositAmountsSubstep =
    findStripeDepositAmountsSubstep(progressSteps);
  const completedDateDisplay = formatOnboardingCompletedDate(
    stripePreferenceSubstep?.completedAt
  );
  const stripeAccountIdForDisplay = isStripeAccountConnected
    ? persistedStripeAccountId
    : (lookedUpStripeAccountId ?? persistedStripeAccountId);
  const mappedSavedDepositAmounts = mapSavedDepositAmounts({
    savedOrderDeposit: stripeDetails?.orderDeposit,
    savedMaxCreditCardAmount: stripeDetails?.maxCreditCardAmount,
    savedMaxAchAmount: stripeDetails?.maxAchAmount,
    savedCartCheckoutDeposit: stripeDetails?.cartCheckoutDeposit,
  });

  useEffect(() => {
    setStripePaymentProcessor(mappedSavedPreference);
    setLocalSavedPreference(mappedSavedPreference);
  }, [mappedSavedPreference]);

  useEffect(() => {
    setDepositAmounts(mappedSavedDepositAmounts);
    setSavedDepositAmounts(mappedSavedDepositAmounts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stripeDetails?.orderDeposit,
    stripeDetails?.maxCreditCardAmount,
    stripeDetails?.maxAchAmount,
    stripeDetails?.cartCheckoutDeposit,
  ]);

  const {
    saveStripeMutation,
    saveDepositAmountsMutation,
    connectStripeAccountMutation,
    disconnectStripeMutation,
  } = useStripeOnboardingMutations({
    dealerCommonId,
    queryClient,
    t,
    actions: {
      setLocalSavedPreference,
      setStripePaymentProcessor,
      setSaveError,
      setIsEditingStripePreferences,
      setShowPreferencesUpdatedBanner,
      setShowDepositSavedBanner,
      setDepositSaveError,
      setSavedDepositAmounts,
      setDepositAmounts,
      setConnectError,
      setShowStripeAccountSetupBanner,
      setIsDisconnectModalOpen,
      setShowStripeDisconnectedBanner,
    },
  });

  const stripeSetupFlags = computeStripeSetupFlags({
    effectiveSavedPreference,
    isStripeAccountConnected,
    isEditingStripePreferences,
    stripePaymentProcessor,
    isFordBrand,
    isDualDealer,
    lookedUpStripeAccountId,
    persistedStripeAccountId,
    isStripeMutationBusy:
      saveStripeMutation.isLoading || disconnectStripeMutation.isLoading,
    dealerCommonId,
    isStripeLookupSettled,
    isStripeEnrollmentActive,
    hasStripeEnrollmentCompletedThisSession,
    stripeAccountFullyEnabled,
  });

  const depositFlags = computeDepositFlags({
    depositAmounts,
    savedDepositAmounts,
    stripeSetupCompleted: stripeSetupFlags.stripeSetupCompleted,
    effectiveSavedPreference,
    isEditingStripePreferences,
    stripeDepositAmountsSubstep,
    showDepositSavedBanner,
    isDepositMutationBusy: saveDepositAmountsMutation.isLoading,
    dealerCommonId,
  });

  const notApplicableLabel = t(`${I18N_BASE}.depositSection.notApplicable`);
  const selectPlaceholderLabel = t(
    `${I18N_BASE}.depositSection.selectPlaceholder`
  );

  const stripeSetupPrefixIcon = useMemo(
    () => (stripeSetupFlags.stripeSetupCompleted ? "check" : "caution"),
    [stripeSetupFlags.stripeSetupCompleted]
  );

  const depositAmountsPrefixIcon = useMemo(
    () =>
      depositFlags.isDepositAmountsNotApplicable ||
      depositFlags.depositAmountsCompleted
        ? "check"
        : "caution",
    [
      depositFlags.isDepositAmountsNotApplicable,
      depositFlags.depositAmountsCompleted,
    ]
  );

  const handleStripeSetupAccordionChange = () => {
    // Ford gate data still loading: open immediately with a loader so the click
    // isn't a dead action; resolution decides whether to block afterward.
    if (isFordGateLoading) {
      setPendingStripeExpand(true);
      setExpandedStripeSetup(true);
      return;
    }
    if (!expandedStripeSetup && shouldBlockLincolnStripeSetup) {
      setIsFordFirstSetupModalOpen(true);
      return;
    }
    setExpandedStripeSetup(!expandedStripeSetup);
  };

  const handleCloseFordFirstSetupModal = () => {
    setIsFordFirstSetupModalOpen(false);
  };

  const handleGoToFordSetup = () => {
    setIsFordFirstSetupModalOpen(false);
    publish("switchOnboardingBrand", "F");
  };

  const handleDepositAmountsAccordionChange = () => {
    setExpandedDepositAmounts(!expandedDepositAmounts);
  };

  const handleStripePaymentProcessorChange = (event) => {
    setStripePaymentProcessor(event.target.value);
    setSaveError(null);
  };

  const handleConnectStripeAccountClick = () => {
    if (
      !lookedUpStripeAccountId ||
      !dealerCommonId ||
      connectStripeAccountMutation.isLoading
    ) {
      return;
    }
    setConnectError(null);
    connectStripeAccountMutation.mutate({
      stripeAccountId: lookedUpStripeAccountId,
      updatedBy,
    });
  };

  const handleStripeCreateAccountStart = () => {
    setIsStripeEnrollmentActive(true);
  };

  const handleStripeAccountReady = ({ accountId }) => {
    if (
      !accountId ||
      !dealerCommonId ||
      connectStripeAccountMutation.isLoading ||
      isStripeAccountConnected
    ) {
      return;
    }
    setIsStripeEnrollmentActive(true);
    setHasStripeEnrollmentCompletedThisSession(true);
    setConnectError(null);
    connectStripeAccountMutation.mutate({
      stripeAccountId: accountId,
      updatedBy,
    });
  };

  const handleStripeAccountStatusChange = ({ isAccountEnabled }) => {
    setStripeAccountFullyEnabled(Boolean(isAccountEnabled));
  };

  const handleEinSelectionChange = (selectedValue) => {
    setEinSelection(selectedValue);
    setConnectError(null);
  };

  const handleDepositAmountChange = (fieldName) => (event) => {
    setDepositAmounts((previousDepositAmounts) => ({
      ...previousDepositAmounts,
      [fieldName]: event.target.value,
    }));
    setShowDepositSavedBanner(false);
    setDepositSaveError(null);
  };

  const handleSaveStripeSetupClick = () => {
    if (stripeSetupFlags.isSaveStripeSetupDisabled) {
      return;
    }

    if (stripeSetupFlags.isDisconnectingFromYes) {
      setSaveError(null);
      setIsDisconnectModalOpen(true);
      return;
    }

    saveStripeMutation.mutate({
      stripePreference: stripePaymentProcessor === "yes" ? "Yes" : "No",
      updatedBy,
    });
  };

  const handleOpenEditPreferences = () => {
    setStripePaymentProcessor(effectiveSavedPreference);
    setShowPreferencesUpdatedBanner(false);
    setShowStripeAccountSetupBanner(false);
    setShowStripeDisconnectedBanner(false);
    setSaveError(null);
    setIsEditingStripePreferences(true);
  };

  const handleCancelEditStripePreferences = () => {
    setStripePaymentProcessor(effectiveSavedPreference);
    setSaveError(null);
    setIsEditingStripePreferences(false);
    setIsDisconnectModalOpen(false);
  };

  const handleCloseDisconnectModal = () => {
    setIsDisconnectModalOpen(false);
  };

  const handleConfirmDisconnectStripe = () => {
    if (!dealerCommonId || disconnectStripeMutation.isLoading) {
      return;
    }
    disconnectStripeMutation.mutate({ updatedBy });
  };

  const handleSaveDepositAmountsClick = () => {
    if (
      !depositFlags.hasAllDepositSelections ||
      !depositFlags.hasPendingDepositChange ||
      !dealerCommonId ||
      saveDepositAmountsMutation.isLoading
    ) {
      return;
    }
    saveDepositAmountsMutation.mutate({
      orderDeposit: Number(depositAmounts.orderDeposit),
      maxCreditCardAmount: Number(depositAmounts.maximumAcceptedCreditCard),
      maxAchAmount: Number(depositAmounts.maximumAcceptedAch),
      cartCheckoutDeposit: Number(depositAmounts.checkoutDeposit),
      updatedBy,
    });
  };

  const einSelectionOptions = useMemo(() => {
    // The One EIN flow reuses the single Stripe account found for the
    // dealership; its details are shown only once the card is selected.
    const oneEinAccountTable = [
      {
        label: t(`${I18N_BASE}.stripeSetup.einSelection.accountIdLabel`),
        value: lookedUpStripeAccountId,
      },
      {
        label: t(`${I18N_BASE}.stripeSetup.einSelection.completedDateLabel`),
        value: completedDateDisplay,
      },
    ];

    return [
      {
        value: "one",
        label: t(`${I18N_BASE}.stripeSetup.einSelection.oneEin`),
        ...(einSelection === "one" && { table: oneEinAccountTable }),
      },
      {
        value: "two",
        label: t(`${I18N_BASE}.stripeSetup.einSelection.twoEins`),
      },
    ];
  }, [t, einSelection, lookedUpStripeAccountId, completedDateDisplay]);

  const depositSaveButtonLabel = getDepositSaveButtonLabel(t, {
    isSaving: saveDepositAmountsMutation.isLoading,
    isSavedState: depositFlags.isDepositAmountsSavedState,
  });

  const depositDropdownDefaults = {
    disabled: depositFlags.areDepositFieldsDisabled,
    displayNotApplicable: depositFlags.isDepositAmountsNotApplicable,
    notApplicableLabel,
    selectPlaceholderLabel,
  };

  return (
    <div className="tw-bg-white tw-rounded-lg tw-p-8">
      <OnboardingPageTitle
        testId="payment-processor-online-deposits-title"
        className="tw-mb-6"
      >
        {t(`${I18N_BASE}.title`)}
      </OnboardingPageTitle>

      <div
        className={styles.accordionList}
        data-testid="onboarding-payment-processor-online-deposits"
      >
        <EMPAccordion
          heading={t(`${I18N_BASE}.stripeSetup.title`)}
          headingClassName={accordionStyles.accordionHeading}
          expanded={expandedStripeSetup}
          onChange={handleStripeSetupAccordionChange}
          prefixIconType={stripeSetupPrefixIcon}
          toggleIconColor="grey"
        >
          <Box
            className={styles.contentContainer}
            data-testid="stripe-setup-content"
          >
            {isFordGateLoading ? (
              <EMPLoader
                loadingMessage={t(
                  `${I18N_BASE}.stripeSetup.gateLoading`,
                  "Loading Stripe setup..."
                )}
              />
            ) : (
              <>
                {stripeSetupFlags.showInitialLincolnNoCaution && (
                  <Box
                    className={styles.cautionBanner}
                    data-testid="stripe-caution-banner"
                  >
                    <EMPBanner
                      severity="warning"
                      title={t(`${I18N_BASE}.stripeSetup.cautionBanner.title`, {
                        currentBrand: brandName,
                      })}
                      content={t(
                        `${I18N_BASE}.stripeSetup.cautionBanner.description`
                      )}
                    />
                  </Box>
                )}

                {stripeSetupFlags.showInitialSetupForm && (
                  <StripePreferenceForm
                    includeSetupDescription
                    t={t}
                    brandName={brandName}
                    radioColor={brandRadioColor}
                    stripePaymentProcessor={stripePaymentProcessor}
                    onPaymentProcessorChange={
                      handleStripePaymentProcessorChange
                    }
                    saveError={saveError}
                    onSave={handleSaveStripeSetupClick}
                    isSaveDisabled={stripeSetupFlags.isSaveStripeSetupDisabled}
                    isSaving={saveStripeMutation.isLoading}
                    isSavedState={stripeSetupFlags.isStripePreferenceSavedState}
                  />
                )}

                {stripeSetupFlags.showEinSelection && (
                  <Box
                    className={styles.einSelectionContainer}
                    data-testid="stripe-ein-selection"
                  >
                    <Typography variant="body1" className={styles.copy}>
                      {t(`${I18N_BASE}.stripeSetup.einSelection.question`)}
                    </Typography>
                    <EMPRadioCardGroup
                      name="stripe-ein-selection"
                      value={einSelection}
                      onChange={handleEinSelectionChange}
                      options={einSelectionOptions}
                      selectedColor={brandRadioColor}
                      ariaLabel={t(
                        `${I18N_BASE}.stripeSetup.einSelection.question`
                      )}
                      testId="stripe-ein-selection-radio-group"
                    />
                    {einSelection === "one" && (
                      <>
                        <Box className={styles.saveButtonContainer}>
                          <EMPButton
                            variant="contained"
                            onClick={handleConnectStripeAccountClick}
                            disabled={
                              !lookedUpStripeAccountId ||
                              connectStripeAccountMutation.isLoading
                            }
                            testId="stripe-ein-connect-account-button"
                            className={styles.saveButton}
                          >
                            {t(
                              `${I18N_BASE}.stripeSetup.accountDetails.connectStripeAccount`
                            )}
                          </EMPButton>
                        </Box>
                        <Typography
                          className={styles.copy}
                          data-testid="stripe-ein-create-account-text"
                        >
                          {t(
                            `${I18N_BASE}.stripeSetup.einSelection.createNewPrefix`
                          )}
                          <a
                            href={STRIPE_ACCOUNT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.createNewAccountLink}
                            style={{ color: brandRadioColor }}
                            data-testid="stripe-ein-create-account-link"
                          >
                            {t(
                              `${I18N_BASE}.stripeSetup.einSelection.createNewLink`
                            )}
                          </a>
                        </Typography>
                        {connectError && (
                          <Typography
                            color="error"
                            variant="body2"
                            data-testid="stripe-ein-connect-error"
                          >
                            {connectError}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                )}

                <StripeAccountDetailsSection
                  showStripeConnectDetails={
                    stripeSetupFlags.showStripeConnectDetails
                  }
                  showStripeAccountDetails={
                    stripeSetupFlags.showStripeAccountDetails
                  }
                  showStripeDeclinedDetails={
                    stripeSetupFlags.showStripeDeclinedDetails
                  }
                  lookedUpStripeAccountId={lookedUpStripeAccountId}
                  stripeAccountIdForDisplay={stripeAccountIdForDisplay}
                  completedDateDisplay={completedDateDisplay}
                  connectError={connectError}
                  onConnect={handleConnectStripeAccountClick}
                  isConnecting={connectStripeAccountMutation.isLoading}
                  brandName={brandName}
                  onEdit={handleOpenEditPreferences}
                  showEditPreferences={!isEditingStripePreferences}
                  showStripeAccountSetupBanner={showStripeAccountSetupBanner}
                  onDismissSuccessBanner={() =>
                    setShowStripeAccountSetupBanner(false)
                  }
                  declinedVariant={stripeSetupFlags.declinedVariant}
                  showStripeDisconnectedBanner={showStripeDisconnectedBanner}
                  onDismissDisconnectedBanner={() =>
                    setShowStripeDisconnectedBanner(false)
                  }
                  showPreferencesUpdatedBanner={showPreferencesUpdatedBanner}
                  onDismissPreferencesUpdatedBanner={() =>
                    setShowPreferencesUpdatedBanner(false)
                  }
                />

                {stripeSetupFlags.showStripeAccountCreateFlow && (
                  <Box data-testid="stripe-account-create-flow">
                    <StripeProvider>
                      <DealerStripeEnrollment
                        user={user}
                        onCreateAccountStart={handleStripeCreateAccountStart}
                        onAccountReady={handleStripeAccountReady}
                        onAccountStatusChange={handleStripeAccountStatusChange}
                        onEditPreferences={handleOpenEditPreferences}
                        showEditPreferences={!isEditingStripePreferences}
                        stripeUrl={STRIPE_ACCOUNT_URL}
                      />
                    </StripeProvider>
                    {connectError && (
                      <Typography
                        color="error"
                        variant="body2"
                        data-testid="stripe-create-connect-error"
                      >
                        {connectError}
                      </Typography>
                    )}
                  </Box>
                )}

                {stripeSetupFlags.showEditYesToNoCaution && (
                  <Box
                    className={styles.cautionBanner}
                    data-testid="stripe-caution-banner"
                  >
                    <EMPBanner
                      severity="warning"
                      title={t(`${I18N_BASE}.stripeSetup.cautionBanner.title`, {
                        currentBrand: brandName,
                      })}
                      content={t(
                        `${I18N_BASE}.stripeSetup.cautionBanner.description`
                      )}
                    />
                  </Box>
                )}

                {stripeSetupFlags.showEditSetupForm && (
                  <StripePreferenceForm
                    includeCancel
                    t={t}
                    brandName={brandName}
                    radioColor={brandRadioColor}
                    stripePaymentProcessor={stripePaymentProcessor}
                    onPaymentProcessorChange={
                      handleStripePaymentProcessorChange
                    }
                    saveError={saveError}
                    onSave={handleSaveStripeSetupClick}
                    isSaveDisabled={stripeSetupFlags.isSaveStripeSetupDisabled}
                    isSaving={saveStripeMutation.isLoading}
                    isSavedState={stripeSetupFlags.isStripePreferenceSavedState}
                    onCancel={handleCancelEditStripePreferences}
                  />
                )}
              </>
            )}
          </Box>
        </EMPAccordion>

        <EMPAccordion
          heading={t(`${I18N_BASE}.depositSection.title`)}
          headingClassName={accordionStyles.accordionHeading}
          expanded={expandedDepositAmounts}
          onChange={handleDepositAmountsAccordionChange}
          prefixIconType={depositAmountsPrefixIcon}
          toggleIconColor="grey"
        >
          <Box
            className={styles.depositAmountsContent}
            data-testid="client-deposit-amounts-content"
          >
            {depositFlags.isDepositAmountsLockedPendingPreference && (
              <EMPConfirmationBanner
                variant="info"
                primaryText={t(
                  `${I18N_BASE}.depositSection.completeStripeSetupFirstBanner.title`
                )}
                secondaryText={t(
                  `${I18N_BASE}.depositSection.completeStripeSetupFirstBanner.description`
                )}
                dismissible={false}
                className={styles.depositNotApplicableBanner}
                testId="client-deposit-amounts-complete-stripe-setup-banner"
              />
            )}

            {depositFlags.isDepositAmountsNotApplicable && (
              <EMPConfirmationBanner
                variant="info"
                primaryText={t(
                  `${I18N_BASE}.depositSection.notApplicableBanner.title`,
                  { currentBrand: brandName }
                )}
                secondaryText={t(
                  `${I18N_BASE}.depositSection.notApplicableBanner.description`
                )}
                dismissible={false}
                className={styles.depositNotApplicableBanner}
                testId="client-deposit-amounts-not-applicable-banner"
              />
            )}

            {showDepositSavedBanner &&
              !depositFlags.areDepositFieldsDisabled && (
                <EMPConfirmationBanner
                  variant="success"
                  primaryText={t(`${I18N_BASE}.depositSection.saveSuccessful`)}
                  closeAriaLabel={t(
                    `${I18N_BASE}.depositSection.closeSaveSuccessfulBanner`
                  )}
                  onClose={() => setShowDepositSavedBanner(false)}
                  testId="client-deposit-amounts-saved-banner"
                />
              )}

            {depositSaveError && !depositFlags.areDepositFieldsDisabled && (
              <Typography
                color="error"
                variant="body2"
                data-testid="client-deposit-amounts-save-error"
              >
                {depositSaveError}
              </Typography>
            )}

            {!depositFlags.isDepositAmountsNotApplicable && (
              <Typography variant="body1" className={styles.copy}>
                {t(`${I18N_BASE}.depositSection.description`)}
              </Typography>
            )}

            <Box className={styles.depositFieldsContainer}>
              <AdvancedAccessDepositField
                isNotApplicable={depositFlags.isDepositAmountsNotApplicable}
                notApplicableLabel={{
                  label: notApplicableLabel,
                  currentBrand: brandName,
                }}
                t={t}
              />

              <DepositDropdownField
                {...depositDropdownDefaults}
                id="order-deposit"
                label={t(`${I18N_BASE}.depositSection.orderDeposit.label`)}
                value={depositAmounts.orderDeposit}
                onChange={handleDepositAmountChange("orderDeposit")}
                options={ORDER_DEPOSIT_OPTIONS}
                infoTitle={t(`${I18N_BASE}.depositSection.orderDeposit.label`)}
                infoCopy={t(
                  `${I18N_BASE}.depositSection.orderDeposit.infoCopy`
                )}
                infoTestId="order-deposit-info"
                selectTestId="order-deposit-select"
              />

              <DepositDropdownField
                {...depositDropdownDefaults}
                id="maximum-accepted-credit-card"
                label={t(
                  `${I18N_BASE}.depositSection.maximumAcceptedCreditCard.label`
                )}
                value={depositAmounts.maximumAcceptedCreditCard}
                onChange={handleDepositAmountChange(
                  "maximumAcceptedCreditCard"
                )}
                options={MAX_CREDIT_CARD_OPTIONS}
                infoTitle={t(
                  `${I18N_BASE}.depositSection.maximumAcceptedCreditCard.label`
                )}
                infoCopy={t(
                  `${I18N_BASE}.depositSection.maximumAcceptedCreditCard.infoCopy`
                )}
                infoTestId="maximum-accepted-credit-card-info"
                selectTestId="maximum-accepted-credit-card-select"
              />

              <DepositDropdownField
                {...depositDropdownDefaults}
                id="maximum-accepted-ach"
                label={t(
                  `${I18N_BASE}.depositSection.maximumAcceptedAch.label`
                )}
                value={depositAmounts.maximumAcceptedAch}
                onChange={handleDepositAmountChange("maximumAcceptedAch")}
                options={MAX_ACH_OPTIONS}
                infoTitle={t(
                  `${I18N_BASE}.depositSection.maximumAcceptedAch.label`
                )}
                infoCopy={t(
                  `${I18N_BASE}.depositSection.maximumAcceptedAch.infoCopy`
                )}
                infoTestId="maximum-accepted-ach-info"
                selectTestId="maximum-accepted-ach-select"
              />

              <DepositDropdownField
                {...depositDropdownDefaults}
                id="checkout-deposit"
                label={t(`${I18N_BASE}.depositSection.checkoutDeposit.label`)}
                value={depositAmounts.checkoutDeposit}
                onChange={handleDepositAmountChange("checkoutDeposit")}
                options={CHECKOUT_DEPOSIT_OPTIONS}
                infoTitle={t(
                  `${I18N_BASE}.depositSection.checkoutDeposit.infoTitle`
                )}
                infoCopy={t(
                  `${I18N_BASE}.depositSection.checkoutDeposit.infoCopy`
                )}
                infoTestId="checkout-deposit-info"
                selectTestId="checkout-deposit-select"
              />
            </Box>

            <Box className={styles.depositSaveButtonContainer}>
              <EMPButton
                variant="contained"
                onClick={handleSaveDepositAmountsClick}
                disabled={depositFlags.isSaveDepositAmountsDisabled}
                testId="save-deposit-amounts-button"
                className={styles.saveButton}
              >
                {depositSaveButtonLabel}
              </EMPButton>
            </Box>
          </Box>
        </EMPAccordion>
      </div>

      <EMPDialog
        open={isFordFirstSetupModalOpen}
        dialogTitle={t(`${I18N_BASE}.stripeSetup.fordFirstSetupModal.title`)}
        dialogTitleSx={{
          whiteSpace: "normal",
          paddingTop: "24px",
          paddingBottom: "12px",
          paddingRight: "48px",
        }}
        dialogHorizontalPadding="24px"
        dialogActionsBottomPadding="24px"
        hasCloseIcon
        handleClose={handleCloseFordFirstSetupModal}
        isFord={false}
        maxWidth={false}
        fullWidth
        paperClassName={styles.fordFirstSetupModalPaper}
        hasSecondaryActionButton
        secondaryActionButtonText={t(
          `${I18N_BASE}.stripeSetup.fordFirstSetupModal.okay`
        )}
        secondaryActionButtonVariant="outlined"
        secondaryActionButtonClassName={styles.fordFirstSetupOkayButton}
        secondaryActionButtonSx={{ minWidth: "auto" }}
        onSecondaryAction={handleCloseFordFirstSetupModal}
        hasActionButton
        actionButtonText={t(
          `${I18N_BASE}.stripeSetup.fordFirstSetupModal.goToFordSetup`
        )}
        actionButtonVariant="contained"
        onConfirm={handleGoToFordSetup}
        dialogActionsDataTestId="stripe-ford-first-setup-modal-actions"
        dialogActionsClassName={styles.fordFirstSetupModalActions}
      >
        <Box
          className={styles.fordFirstSetupModalBody}
          data-testid="stripe-ford-first-setup-modal-body"
        >
          <Typography
            className={`${styles.copy} ${styles.fordFirstSetupModalBodyText}`}
          >
            {t(`${I18N_BASE}.stripeSetup.fordFirstSetupModal.body`)}
          </Typography>
        </Box>
      </EMPDialog>

      <StripeDisconnectModal
        isOpen={isDisconnectModalOpen}
        t={t}
        isFordBrand={isFordBrand}
        onClose={handleCloseDisconnectModal}
        onConfirm={handleConfirmDisconnectStripe}
        isConfirming={disconnectStripeMutation.isLoading}
      />
    </div>
  );
};

OnboardingPaymentProcessorOnlineDeposits.propTypes = {
  savedStripePreference: PropTypes.string,
  progressSteps: PropTypes.arrayOf(PropTypes.object),
};

export default OnboardingPaymentProcessorOnlineDeposits;
